import { create } from 'zustand';
import { applyCanonical, type WhoMergeMap } from '@/lib/who-match';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';

// WhoMergeMap + the pure merge-map reducer/normalizer moved to the who-match
// leaf in P0 (docs/plans/people-alias-suggestion-audit-2026-06-08.md) so the
// probe tests the SAME reducer the store runs. Re-exported so existing
// importers keep their reference stable.
export type { WhoMergeMap } from '@/lib/who-match';

const TEXT_SCALE_KEY = 'adia-text-scale';
const BIBLE_THEME_KEY = 'adia-bible-theme';
const WHO_MERGE_KEY = 'adia-who-merge';
const WHO_DISMISS_KEY = 'adia-who-dismissed-pairs';
const LEAF_GLOW_SEEN_KEY = 'adia-leaf-glow-seen';
const VERSE_COACHMARK_SEEN_KEY = 'adia-bible-verse-coachmark-seen';
// How long service-failure fallback stays active before we retry enhanced.
// Short enough that users aren't stuck in degraded mode; long enough that
// we don't re-hit a broken server immediately on the next tap.
const SERVICE_FALLBACK_MS = 60_000;

function readVerseCoachmarkSeen(): boolean {
  // One-time first-run coachmark teaching the Bible-reader verse gesture
  // (Chunk D). Default false so a fresh reader is taught once; true only
  // after the user dismisses the banner X or opens the verse panel.
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(VERSE_COACHMARK_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Three-step text-size preference. Scales all body typography via rem —
 * readable prayer/update/testimony surfaces multiply with the root
 * font-size while chrome (buttons, pills, safe-area math, touch targets)
 * stays in px so layouts stay intact. See CLAUDE.md rule on text scale.
 */
export type TextScale = 'default' | 'large' | 'xl';

function readTextScale(): TextScale {
  if (typeof window === 'undefined') return 'default';
  try {
    const v = localStorage.getItem(TEXT_SCALE_KEY);
    return v === 'large' || v === 'xl' ? v : 'default';
  } catch {
    return 'default';
  }
}

/**
 * Reader theme preference (Bible reader rethink, Chunk A). Reader-scoped: applied
 * to the chapter reader's .container via data-bible-theme, NOT html, so the rest
 * of the app stays dark. Persisted to `adia-bible-theme`.
 *
 * Ships default 'night' so the reading surface is byte-identical to current prod;
 * the default flips to 'paper' after the operator's device preview, which is a
 * one-line change here (both returns below). No `document` side-effect and no
 * applyInitial: BibleChapter reads this reactively and React applies the
 * attribute on the reader container at first render, so there is no FOUC.
 */
export type BibleTheme = 'paper' | 'night' | 'light';

function readBibleTheme(): BibleTheme {
  // Default = light (operator device-preview verdict 2026-06-19, overriding the
  // planned paper default). A stored value still wins when present.
  if (typeof window === 'undefined') return 'light';
  try {
    const v = localStorage.getItem(BIBLE_THEME_KEY);
    return v === 'paper' || v === 'light' || v === 'night' ? v : 'light';
  } catch {
    return 'light';
  }
}

/**
 * CR raw quarantine capture (PC-B rev 9 section 12 item 1). Returns the
 * ORIGINAL localStorage string VERBATIM — no JSON.parse, no shape filter,
 * no normalization. A malformed or partially-well-formed legacy value is
 * preserved byte-for-byte as evidence; any inspection metadata is DERIVED
 * on demand (src/lib/legacy-grouping-notice.ts) and never replaces it.
 * Read by NOTHING except that derivation and (later) S6 adoption.
 *
 * The prior `readWhoMergeMap()` / `readDismissedPairs()` boot readers and
 * their writers are RETIRED for the CR window: they parsed and FILTERED,
 * which destroys exactly the evidence the quarantine preserves.
 */
function captureRawLegacyString(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Set of dismissed alias suggestion pairs. Keys are the stable
 * `aliasPairKey(a, b)` form from prayer-utils (sorted-joined). Once a
 * user says "Keep separate" on the Person-profile merge suggestion for
 * Mom/Mama, that pair is added here so we never prompt again. Persists
 * to `adia-who-dismissed-pairs` as a JSON array.
 */
/**
 * Set of prayer ids whose Sacred Leaf button has already been seen
 * (i.e. PrayerDetail has mounted at least once for that prayer). The
 * LeafButton's breathing-glow is a one-time discovery affordance —
 * once a prayer is in this set, subsequent visits skip the glow.
 * Persisted to `adia-leaf-glow-seen` as a JSON array. See PrayerDetail's
 * mark-on-mount useEffect for the write semantics.
 */
function readLeafGlowSeenIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(LEAF_GLOW_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const result = new Set<string>();
    for (const v of parsed) {
      if (typeof v === 'string' && v) result.add(v);
    }
    return result;
  } catch {
    return new Set();
  }
}

function writeLeafGlowSeenIds(ids: Set<string>): void {
  try {
    localStorage.setItem(LEAF_GLOW_SEEN_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Private-mode / disabled-storage: in-memory only.
  }
}

/**
 * Why enhanced voice is temporarily unavailable.
 *
 * - `offline`: device lost network. Cleared on the `online` event.
 * - `service`: a recent transcribe call failed; retry the next tap
 *   after a 60s grace window so the next attempt routes through xAI
 *   again. Auto-clears via `serviceFallbackTimer`.
 * - `unauthenticated`: user has no Supabase session. V.A.2 routes
 *   audio uploads through Storage which requires auth, so anonymous
 *   users fall through to browser-native Web Speech. Cleared by
 *   `sync.ts`'s `onAuthStateChange` handler when a SIGNED_IN event
 *   fires (or any auth event that carries a session).
 */
export type FallbackReason = 'offline' | 'service' | 'unauthenticated' | null;

interface SettingsState {
  /**
   * One-time seen-flag for the Bible-reader verse-gesture coachmark
   * (Chunk D). Persisted to `adia-bible-verse-coachmark-seen`. Marked true
   * on the first verse-panel open or the banner's explicit dismiss, never
   * on mount. Read reactively by BibleChapter to gate the coachmark banner.
   */
  verseCoachmarkSeen: boolean;
  markVerseCoachmarkSeen: () => void;
  /**
   * Body-text size preference. Persisted to `adia-text-scale`. Applied to
   * <html data-text-scale="..."> by main.tsx on startup and on every
   * change, which drives the root font-size; every rem-based font-size
   * in the app scales with it.
   */
  textScale: TextScale;
  setTextScale: (scale: TextScale) => void;
  /**
   * Bible reader theme (persisted to `adia-bible-theme`). Applied to the chapter
   * reader's .container via data-bible-theme (reader-scoped, not html). Read
   * reactively by BibleChapter; no document side-effect here.
   */
  bibleTheme: BibleTheme;
  setBibleTheme: (theme: BibleTheme) => void;
  /**
   * CR raw quarantine (PC-B rev 9 section 12 item 1): the ORIGINAL
   * `adia-who-merge` / `adia-who-dismissed-pairs` localStorage strings,
   * captured VERBATIM at store init with no parse and no filter. These
   * fields are the preserved evidence; they are read by NOTHING except
   * the pure notice derivation (legacy-grouping-notice.ts) and, later,
   * S6 adoption. Never written after init; never re-persisted.
   */
  whoMergeMapRaw: string | null;
  dismissedAliasPairsRaw: string | null;
  /**
   * ACTIVE identity projection — EMPTY for the whole CR window
   * (PC-B rev 9 section 12). Every reader, whether it goes through
   * `getWhoMergeMap()` / `canonicalizeWho`'s default parameter or selects
   * this field directly (RememberStream, Garden, MergePersonSheet), gets
   * empty BY CONSTRUCTION: the raw quarantine above is a separate field,
   * so no access path can reach the legacy bytes.
   */
  whoMergeMap: WhoMergeMap;
  /**
   * SOURCE-LEVEL INERT for the CR window (PC-B rev 9 section 12 item 2):
   * the action BODY performs no legacy-key write and no active-state
   * write. Every merge/undo entry surface is also hidden, but the
   * guarantee lives here — a direct `getState().mergeWhoAliases(...)`
   * invocation from code the surface inventory missed writes nothing.
   * The event-log replacement ships with S5.
   */
  mergeWhoAliases: (aliases: string[], canonical: string) => void;
  /** SOURCE-LEVEL INERT for the CR window (zero call sites is a snapshot, not a guarantee). */
  unmergeWhoAlias: (alias: string) => void;
  /** SOURCE-LEVEL INERT for the CR window (the merge-undo toast is unreachable). */
  setWhoMergeMap: (map: WhoMergeMap) => void;
  /**
   * Set of dismissed alias suggestion pairs (pairKey from
   * prayer-utils.aliasPairKey). When a user taps "Keep separate" on a
   * Person-profile suggestion, the pair is added here so we never
   * re-suggest it. Persisted to `adia-who-dismissed-pairs`.
   */
  dismissedAliasPairs: Set<string>;
  /** SOURCE-LEVEL INERT for the CR window (the suggestion row is hidden). */
  dismissAliasPair: (pairKey: string) => void;
  /**
   * Set of prayer ids whose LeafButton breathing-glow has been seen.
   * Persisted to `adia-leaf-glow-seen`. Marked on PrayerDetail mount
   * so subsequent visits to the same prayer skip the glow. Cleared
   * by the corresponding prayer's removePrayer call so an undo +
   * reopen re-triggers the glow.
   */
  leafGlowSeenIds: Set<string>;
  markLeafGlowSeen: (id: string) => void;
  forgetLeafGlowSeen: (id: string) => void;
  /**
   * Runtime-only override (never persisted). When non-null, enhanced voice
   * is temporarily bypassed for the current session and the mic falls back
   * to browser-native Web Speech. Restoring is automatic (online event or
   * auto-expire).
   */
  fallbackReason: FallbackReason;
  setFallbackReason: (reason: FallbackReason) => void;
}

// Module-scoped timer handle so we can cancel pending auto-clears when the
// fallback reason changes (e.g. offline event fires while a service-timer
// is still counting).
let serviceFallbackTimer: ReturnType<typeof setTimeout> | null = null;

export const useSettingsStore = create<SettingsState>((set) => ({
  verseCoachmarkSeen: readVerseCoachmarkSeen(),
  markVerseCoachmarkSeen: () => {
    try {
      localStorage.setItem(VERSE_COACHMARK_SEEN_KEY, 'true');
    } catch {
      // Private-mode / disabled-storage: keep in-memory only.
    }
    set({ verseCoachmarkSeen: true });
  },
  textScale: readTextScale(),
  setTextScale: (scale) => {
    try {
      localStorage.setItem(TEXT_SCALE_KEY, scale);
    } catch {
      // Private-mode / disabled-storage: keep in-memory only.
    }
    // Apply to <html data-text-scale="..."> immediately so CSS picks up
    // the new root font-size without waiting for the next render cycle.
    if (typeof document !== 'undefined') {
      if (scale === 'default') {
        document.documentElement.removeAttribute('data-text-scale');
      } else {
        document.documentElement.setAttribute('data-text-scale', scale);
      }
    }
    set({ textScale: scale });
  },
  bibleTheme: readBibleTheme(),
  setBibleTheme: (theme) => {
    try {
      localStorage.setItem(BIBLE_THEME_KEY, theme);
    } catch {
      // Private-mode / disabled-storage: keep in-memory only.
    }
    // No document side-effect (unlike setTextScale): the attribute lives on the
    // React-rendered reader container, so BibleChapter applies it on re-render.
    set({ bibleTheme: theme });
  },
  // CR (PC-B rev 9 section 12): init captures the raw localStorage strings
  // VERBATIM into the RAW quarantine fields; the ACTIVE fields initialize
  // empty and stay empty for the whole CR window.
  whoMergeMapRaw: captureRawLegacyString(WHO_MERGE_KEY),
  dismissedAliasPairsRaw: captureRawLegacyString(WHO_DISMISS_KEY),
  whoMergeMap: {},
  mergeWhoAliases: (aliases, canonical) => {
    // SOURCE-LEVEL INERT (CR, PC-B rev 9 section 12 item 2). No legacy-key
    // write, no active-state write. Deliberately not removed: a removed
    // action would crash a missed caller; an inert one contains it.
    void aliases;
    void canonical;
  },
  unmergeWhoAlias: (alias) => {
    // SOURCE-LEVEL INERT (CR). The action body wrote to the legacy key
    // before CR; zero call sites was a snapshot, not a guarantee.
    void alias;
  },
  setWhoMergeMap: (map) => {
    // SOURCE-LEVEL INERT (CR). Snapshot-restore undo is unreachable once
    // the merge entry surfaces are hidden; the body guarantees it anyway.
    void map;
  },
  dismissedAliasPairs: new Set<string>(),
  dismissAliasPair: (pairKey) => {
    // SOURCE-LEVEL INERT (CR). The alias-suggestion row is hidden for the
    // CR window (nothing to suggest against, nothing to dismiss).
    void pairKey;
  },
  leafGlowSeenIds: readLeafGlowSeenIds(),
  markLeafGlowSeen: (id) => {
    if (!id) return;
    const current = useSettingsStore.getState().leafGlowSeenIds;
    if (current.has(id)) return;
    const next = new Set(current);
    next.add(id);
    writeLeafGlowSeenIds(next);
    set({ leafGlowSeenIds: next });
  },
  forgetLeafGlowSeen: (id) => {
    if (!id) return;
    const current = useSettingsStore.getState().leafGlowSeenIds;
    if (!current.has(id)) return;
    const next = new Set(current);
    next.delete(id);
    writeLeafGlowSeenIds(next);
    set({ leafGlowSeenIds: next });
  },
  fallbackReason: null,
  setFallbackReason: (reason) => {
    if (serviceFallbackTimer) {
      clearTimeout(serviceFallbackTimer);
      serviceFallbackTimer = null;
    }
    set({ fallbackReason: reason });
    // Service-failure fallback auto-clears so we retry enhanced on the
    // next tap after the grace window. Offline fallback clears only on
    // the online event (see main.tsx listeners).
    if (reason === 'service') {
      serviceFallbackTimer = setTimeout(() => {
        // Only clear if still 'service' — an offline event may have
        // already overridden with a more specific reason.
        if (useSettingsStore.getState().fallbackReason === 'service') {
          set({ fallbackReason: null });
        }
        serviceFallbackTimer = null;
      }, SERVICE_FALLBACK_MS);
    }
  },
}));

/**
 * Non-reactive snapshot — has the LeafButton breathing-glow already
 * been seen for this prayer? Read in PrayerDetail's useState
 * initializer (so the captured value drives the LeafButton's
 * `breathing` prop on first paint).
 */
export function getLeafGlowSeen(id: string): boolean {
  return useSettingsStore.getState().leafGlowSeenIds.has(id);
}

/** Refresh owner-scoped browser settings before an owner runtime opens. */
export function reloadOwnerScopedSettingsMemory(): void {
  useSettingsStore.setState({
    whoMergeMapRaw: captureRawLegacyString(WHO_MERGE_KEY),
    dismissedAliasPairsRaw: captureRawLegacyString(WHO_DISMISS_KEY),
    whoMergeMap: {},
    dismissedAliasPairs: new Set<string>(),
    leafGlowSeenIds: readLeafGlowSeenIds(),
  });
}

/**
 * Non-reactive snapshot of the ACTIVE who merge map. Under CR this is
 * empty by construction (the raw quarantine is a separate field), so
 * every consumer resolves to baseline grouping. Pure-helper callers pass
 * the result through `canonicalizeWho` so display surfaces see the
 * resolved name without touching Zustand directly.
 */
export function getWhoMergeMap(): WhoMergeMap {
  return useSettingsStore.getState().whoMergeMap;
}

/**
 * Apply the merge map to a raw display name. Returns the canonical form
 * if the alias has been merged; otherwise returns the name unchanged.
 * Null/empty passthrough so callers don't need guards.
 */
export function canonicalizeWho(name: string, map: WhoMergeMap = getWhoMergeMap()): string {
  // Store-aware convenience wrapper over the pure `applyCanonical` leaf
  // helper (default map = the live merge map). Keeps the call sites that
  // import `canonicalizeWho` from settings-store unchanged.
  return applyCanonical(name, map);
}

/**
 * Apply the persisted text-scale to <html> before React renders. Called
 * eagerly from main.tsx so the root font-size is correct on the very
 * first paint — otherwise rem-based typography briefly renders at default
 * scale, then jumps to the user's preference, which flashes visibly.
 */
export function applyInitialTextScale(): void {
  if (typeof document === 'undefined') return;
  const scale = readTextScale();
  if (scale !== 'default') {
    document.documentElement.setAttribute('data-text-scale', scale);
  }
}

/**
 * EFFECTIVE enhanced voice — true only when the deployment explicitly opts
 * in and no runtime fallback is active. The independent deployment gate is
 * load-bearing: enabling typed core writes must not also enable cloud audio.
 * When false, the mic routes to browser-native Web Speech when available.
 */
export function getEffectiveEnhancedVoice(): boolean {
  return enhancedVoiceAllowed() &&
    useSettingsStore.getState().fallbackReason === null;
}
